import { mapRecordFromServer, mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, GetOneResponse, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase } from 'core/utils';
import {
  IJob,
  IJobCustomer,
  IJobFile,
  IJobRequestPayload,
  IJobStatus,
  IJobVendor,
  IRenameJobData,
  IUpsertJobCustomerPayload,
  IUpsertJobVendorPayload,
} from 'jobs/types';

export const getJobs = async (requestPayload: IRequestPayload): Promise<GetManyResponse<IJob>> => {
  const { data } = await partfinitiClient.get(
    `/bom/list/boms?${getQueryStringToSnakeCase(requestPayload)}`
  );

  return mapRecordsFromServer<IJob>(data.data, data.recordsFiltered);
};

export const getJob = async (
  requestPayload: IJobRequestPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IJob>> => {
  const { data } = await partfinitiClient.get(
    `/bom/list/boms?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordFromServer<IJob>(data.data?.[0]);
};

export const getJobStatuses = async (
  requestPayload: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IJobStatus>> => {
  const { data } = await partfinitiClient.get(
    `/corporate_v2/bom-status-list/statuses?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IJobStatus>(data.data, data.recordsFiltered);
};

export const getJobCustomers = async (
  requestPayload: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IJobCustomer>> => {
  const { data } = await partfinitiClient.get(
    `/bom/customer-company-association-list/associations?${getQueryStringToSnakeCase(
      requestPayload
    )}`,
    signal
  );

  return mapRecordsFromServer<IJobCustomer>(data.data, data.recordsFiltered);
};

export const upsertJobCustomer = async (payload: IUpsertJobCustomerPayload) =>
  partfinitiClient.postForm('/bom/customer-company-association-edit/save', payload, {
    includeNullValues: true,
  });

export const removeJobCustomers = async (ids: number[]) =>
  partfinitiClient.postForm('/bom/customer-company-association-edit/remove', { ids });

export const getJobVendors = async (
  requestPayload: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IJobVendor>> => {
  const { data } = await partfinitiClient.get(
    `/bom/vendor-company-association-list/associations?${getQueryStringToSnakeCase(
      requestPayload
    )}`,
    signal
  );

  return mapRecordsFromServer<IJobVendor>(data.data, data.recordsFiltered);
};

export const upsertJobVendor = async (payload: IUpsertJobVendorPayload) =>
  partfinitiClient.postForm('/bom/vendor-company-association-edit/save', payload, {
    includeNullValues: true,
  });

export const removeJobVendors = async (ids: number[]) =>
  partfinitiClient.postForm('/bom/vendor-company-association-edit/remove', { ids });

export const upsertJob = async (data: { bom: Partial<IJob> }) =>
  partfinitiClient.postForm('/bom/edit/save', data, {
    includeNullValues: true,
  });

export const upsertJobs = async (data: { boms: Partial<IJob>[] }) =>
  partfinitiClient.postForm('/bom/edit/save-bulk', data);

export const renameJob = async (data: { bom: Partial<IRenameJobData> }) =>
  partfinitiClient.postForm('/bom/edit/rename', data);

export const exportJobLog = async (data: Partial<IJob>) =>
  (await partfinitiClient.postForm('/bom/log-list/start-generating-bom-log-export-file', data))
    .cliJob;

export const getJobFiles = async (
  requestPayload: { filters: { bomId: number } },
  signal?: AbortSignal
): Promise<GetManyResponse<IJobFile>> => {
  const { data } = await partfinitiClient.get(
    `/bom/list/attached-files?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IJobFile>(data?.data, data?.recordsFiltered);
};

export const attachJobFile = async (data: { bomId: number; file: number }) =>
  partfinitiClient.postForm('/bom/edit/attach-file', data);

export const detachJobFile = async (data: { ids: number[] }) =>
  partfinitiClient.postForm('/bom/edit/detach-files', data);
